import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { planDisplayName } from '../../lib/orgBillingConstants';

interface Tenant {
  id: string;
  name: string;
  domain: string;
  questionnaireCompleted: boolean;
  plan: string;
  subscriptionStatus: string;
  memberCount: number;
  sessionCount: number;
  createdAt: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    callCount: number;
    costUsd: number;
  };
}

export function AdminTenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadTenants = useCallback(
    () =>
      api
        .get('/admin/tenants')
        .then((res) => setTenants(res.data))
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    setLoading(true);
    loadTenants().finally(() => setLoading(false));
  }, [loadTenants]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(id);
  }, [toast]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await api.post<{
        name: string;
        plan?: string;
        subscriptionStatus?: string;
        trialEndsAt?: string;
      }>('/admin/tenants', {
        name: trimmed,
        ...(domain.trim() ? { domain: domain.trim() } : {}),
      });
      setName('');
      setDomain('');
      await loadTenants();

      const created = res.data;
      const plan = planDisplayName(created.plan ?? 'pro');
      const trialEnd = created.trialEndsAt
        ? new Date(created.trialEndsAt).toLocaleDateString()
        : null;
      setToast(
        `"${created.name}" created on the ${plan} plan${trialEnd ? ` with a trial ending ${trialEnd}` : ''}.`,
      );
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setCreateError(msg || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return <div className="text-gray-500">Loading...</div>;
  }

  return (
    <div>
      {toast && (
        <div
          role="status"
          className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg shadow-lg text-sm max-w-md animate-[fadeIn_0.2s_ease-out]"
        >
          <svg
            className="w-5 h-5 shrink-0 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{toast}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-auto text-green-600 hover:text-green-800"
            aria-label="Dismiss"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Organizations</h1>
          <p className="text-gray-600 mt-1">
            {tenants.length} organization{tenants.length !== 1 ? 's' : ''} on the platform.
          </p>
        </div>
        <Link to="/admin" className="text-sm text-gray-600 hover:text-gray-900">
          Back to Admin
        </Link>
      </div>

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Create organization</h2>
        <p className="text-sm text-gray-600 mb-4">
          Name is required. Optionally set the customer&apos;s email domain (e.g. acme.com) so users
          signing in with that domain join this organization automatically.
        </p>
        <form
          onSubmit={handleCreate}
          className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
        >
          <div className="flex-1 min-w-[200px]">
            <label
              htmlFor="new-organization-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name
            </label>
            <input
              id="new-organization-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="Acme Corp"
              disabled={creating}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label
              htmlFor="new-organization-domain"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email domain <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="new-organization-domain"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="acme.com"
              disabled={creating}
            />
          </div>
          <button
            type="submit"
            disabled={creating || !name.trim()}
            className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </form>
        {createError && <p className="mt-3 text-sm text-red-600">{createError}</p>}
      </div>

      {tenants.length === 0 ? (
        <div className="bg-white p-12 rounded-lg border text-center">
          <p className="text-gray-500">No organizations yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-gray-500 font-medium">
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3 text-center">Members</th>
                <th className="px-4 py-3 text-center">Sessions</th>
                <th className="px-4 py-3 text-right">Est. AI $</th>
                <th className="px-4 py-3 text-center">Onboarding</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tenants/${t.id}`}
                      className="font-medium text-gray-900 hover:text-red-700"
                    >
                      {t.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{t.domain}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <span className="font-medium">{planDisplayName(t.plan)}</span>
                    <span className="text-gray-400 text-xs block">{t.subscriptionStatus}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">{t.memberCount}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{t.sessionCount}</td>
                  <td className="px-4 py-3 text-right text-gray-800 font-medium">
                    {t.usage ? `$${t.usage.costUsd.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.questionnaireCompleted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {t.questionnaireCompleted ? 'Complete' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
